// src/features/sessions/api/sessionsApi.ts
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../config/api';
import { brand } from '../../../config/brand';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL !== undefined
  ? import.meta.env.VITE_API_BASE_URL
  : 'https://autopilot-api.azurewebsites.net';

const SESSIONS_BASE = `${API_BASE_URL}/sessions/remote`;

export interface RemoteSession {
  id: string;
  brand?: string;
  status: 'provisioning' | 'running' | 'stopping' | 'stopped' | 'failed';
  fqdn: string | null;
  accessUrl: string | null;
  ttlSeconds: number;
  expiresAt: string;
  config: {
    gitRepo?: string | null;
  };
  createdAt: string;
  startedAt: string | null;
  stoppedAt: string | null;
}

export interface CreateSessionOptions {
  gitRepo?: string;
  ttlSeconds?: number;
}

export async function createRemoteSession(options: CreateSessionOptions = {}): Promise<{
  success: boolean;
  session: RemoteSession;
  message?: string;
}> {
  // Attach the compiled-in brand id so the backend can launch the matching
  // ACI image and the container ships with the portal's branding. Omitted
  // brand is treated as 'autopilot' server-side for legacy clients.
  const response = await apiClient.post(SESSIONS_BASE, { ...options, brand: brand.id });
  return response.data;
}

export async function listRemoteSessions(): Promise<{
  success: boolean;
  sessions: RemoteSession[];
}> {
  // Filter the list to this portal's brand so Onbuzz users don't see
  // Autopilot-launched sessions in their portal and vice versa. Admin /
  // debug callers can hit the endpoint directly without `?brand=` to see
  // everything.
  const response = await apiClient.get(SESSIONS_BASE, { params: { brand: brand.id } });
  return response.data;
}

export async function getRemoteSession(id: string): Promise<{
  success: boolean;
  session: RemoteSession;
}> {
  const response = await apiClient.get(`${SESSIONS_BASE}/${id}`);
  return response.data;
}

export async function terminateRemoteSession(id: string): Promise<{
  success: boolean;
  message: string;
}> {
  const response = await apiClient.delete(`${SESSIONS_BASE}/${id}`);
  return response.data;
}

export async function extendRemoteSession(id: string, additionalSeconds = 3600): Promise<{
  success: boolean;
  session: { id: string; expiresAt: string; ttlSeconds: number };
}> {
  const response = await apiClient.post(`${SESSIONS_BASE}/${id}/extend`, { additionalSeconds });
  return response.data;
}
